import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Plus, Trash2, DollarSign } from "lucide-react";
import { fmtMoney, CURRENCIES } from "@/lib/currency";

const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function LedgerTracker() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [entries, setEntries] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [draft, setDraft] = useState({ type: "sale", date: new Date().toISOString().slice(0,10), amount: "", category: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/ledger", { params: { month } }).then((r) => setEntries(r.data));

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    if (user.currency) setCurrency(user.currency);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, nav, month]);

  const add = async () => {
    if (!draft.amount || !draft.date) { toast.error("Enter an amount and date"); return; }
    setBusy(true);
    try {
      await api.post("/ledger", { ...draft, amount: parseFloat(draft.amount) });
      toast.success("Added");
      setDraft({ ...draft, amount: "", category: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const del = async (id) => { if (!window.confirm("Delete this entry?")) return; await api.delete(`/ledger/${id}`); load(); };

  const totals = useMemo(() => {
    const sales = entries.filter((e) => e.type === "sale").reduce((s, e) => s + (e.amount || 0), 0);
    const expenses = entries.filter((e) => e.type === "expense").reduce((s, e) => s + (e.amount || 0), 0);
    return { sales, expenses, profit: sales - expenses };
  }, [entries]);

  const monthsBack = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    return d.toISOString().slice(0, 7);
  });

  if (loading || !user) return null;

  return (
    <>
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><TrendingUp className="w-3.5 h-3.5" style={{ color: "#4C9F70" }} /><span>Business tool</span></div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">Sales & <span style={{ color: "#4C9F70" }}>Expense</span> Tracker</h1>
            <p className="mt-2 text-[#857163] max-w-2xl">Log sales and expenses. Simple monthly P&L at a glance.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={month} onChange={(e) => setMonth(e.target.value)} className="cp-input !py-1.5 !text-sm max-w-[160px]" data-testid="month-select">
              {monthsBack.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="cp-input !py-1.5 !text-xs max-w-[130px]" data-testid="currency-select">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card label="Sales" value={fmtMoney(totals.sales, currency)} color="#4C9F70" />
          <Card label="Expenses" value={fmtMoney(totals.expenses, currency)} color="#C2185B" />
          <Card label="Net profit" value={fmtMoney(totals.profit, currency)} color={totals.profit >= 0 ? "#4C9F70" : "#C2185B"} big />
        </div>

        <div className="cp-card p-4 mb-4">
          <div className="text-sm font-medium text-[#3B2C24] mb-2">Add entry</div>
          <div className="grid grid-cols-2 sm:grid-cols-[100px_130px_110px_1fr_110px_60px] gap-2">
            <select className="cp-input !py-1.5 !text-sm" value={draft.type} onChange={(e) => setDraft({...draft, type: e.target.value})} data-testid="draft-type">
              <option value="sale">Sale</option><option value="expense">Expense</option>
            </select>
            <input type="date" className="cp-input !py-1.5 !text-sm" value={draft.date} onChange={(e) => setDraft({...draft, date: e.target.value})} data-testid="draft-date" />
            <input type="number" step={0.01} className="cp-input !py-1.5 !text-sm" placeholder="Amount" value={draft.amount} onChange={(e) => setDraft({...draft, amount: e.target.value})} data-testid="draft-amount" />
            <input className="cp-input !py-1.5 !text-sm" placeholder="Note (optional)" value={draft.note} onChange={(e) => setDraft({...draft, note: e.target.value})} data-testid="draft-note" />
            <input className="cp-input !py-1.5 !text-sm" placeholder="Category" value={draft.category} onChange={(e) => setDraft({...draft, category: e.target.value})} data-testid="draft-category" />
            <button onClick={add} disabled={busy} className="rounded-full text-white text-sm font-semibold disabled:opacity-50" style={{ background: "#4C9F70" }} data-testid="add-entry"><Plus className="w-4 h-4 inline" /></button>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="cp-card p-8 text-center text-[#857163]">No entries for {month} yet.</div>
        ) : (
          <div className="cp-card overflow-hidden">
            {entries.map((e) => (
              <div key={e.id} className="grid grid-cols-[80px_100px_1fr_120px_40px] gap-3 px-4 py-2.5 border-b border-[#EBDCD1] items-center last:border-0" data-testid={`entry-${e.id}`}>
                <div className={`text-xs font-semibold uppercase ${e.type === "sale" ? "text-[#4C9F70]" : "text-[#C2185B]"}`}>{e.type}</div>
                <div className="text-xs text-[#857163] font-mono">{e.date}</div>
                <div className="text-sm text-[#3B2C24] truncate">{e.note || <span className="text-[#857163] italic">—</span>}{e.category && <span className="text-xs text-[#857163] ml-2">· {e.category}</span>}</div>
                <div className={`text-right font-mono font-semibold tabular-nums ${e.type === "sale" ? "text-[#4C9F70]" : "text-[#C2185B]"}`}>{e.type === "expense" ? "−" : "+"}{fmtMoney(e.amount, currency)}</div>
                <button onClick={() => del(e.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-full"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  );
}

function Card({ label, value, color, big }) {
  return (
    <div className="cp-card p-4">
      <div className="text-xs uppercase tracking-widest text-[#857163]">{label}</div>
      <div className={`font-heading font-bold mt-1 tabular-nums ${big ? "text-3xl" : "text-2xl"}`} style={{ color }}>{value}</div>
    </div>
  );
}
